<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import {
  ANALYSIS_STEPS,
  usePlanAnalysisState,
} from "@/stores/PlanAnalysisState";

/**
 * Journal des étapes de l'analyse, affiché sur le plan pendant le balayage :
 * étapes faites (coche, détail chiffré conservé), en cours (spinner), à
 * venir (estompées). L'inspecteur voit que « ça travaille », et quoi.
 */

const { t } = useTranslation();
const state = usePlanAnalysisState();

const currentIndex = computed(() =>
  state.analysisStep ? ANALYSIS_STEPS.indexOf(state.analysisStep) : -1,
);

const statusOf = (index: number): "done" | "current" | "pending" => {
  if (index < currentIndex.value) return "done";
  if (index === currentIndex.value) return "current";
  return "pending";
};
</script>

<template>
  <div class="progress-steps">
    <div
      v-for="(step, index) in ANALYSIS_STEPS"
      :key="step"
      class="step d-flex align-items-center gap-2"
      :class="statusOf(index)"
    >
      <span class="step-icon d-inline-flex align-items-center justify-content-center">
        <PhosphorIcon
          v-if="statusOf(index) === 'done'"
          name="check"
          :size="13"
          weight="bold"
        />
        <span
          v-else-if="statusOf(index) === 'current'"
          class="spinner-border spinner-border-sm"
          role="status"
        />
        <PhosphorIcon v-else name="circle" :size="8" weight="fill" />
      </span>
      <span class="step-label">{{ t(`canvas:analysis.steps.${step}`) }}</span>
      <span v-if="state.analysisStepDetails[step]" class="step-detail">
        {{ state.analysisStepDetails[step] }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.progress-steps {
  position: absolute;
  left: 50%;
  bottom: 28px;
  transform: translateX(-50%);
  min-width: 300px;
  max-width: min(92vw, 460px);
  padding: 12px 16px;
  border-radius: 12px;
  background: rgba(8, 6, 18, 0.78);
  border: 1px solid color-mix(in srgb, var(--dr-report) 55%, transparent);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45);
  color: var(--dr-report-light);
  font-size: 0.84rem;
  pointer-events: none;
}

.step {
  padding: 3px 0;
  transition: opacity 0.2s ease;

  &.pending {
    opacity: 0.35;
  }
  &.done {
    opacity: 0.75;
  }
  &.current .step-label {
    font-weight: 600;
  }
}

.step-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;

  .spinner-border {
    width: 13px;
    height: 13px;
    border-width: 2px;
    color: var(--dr-report-light);
  }
}

.step-detail {
  margin-left: auto;
  padding-left: 12px;
  font-size: 0.76rem;
  opacity: 0.8;
  white-space: nowrap;
}
</style>
